import { createContext, useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { logout as logoutApi } from "@/api/auth";
import { ROUTES } from "@/common/constants/routes";
import { TUserData } from "@/common/types/user";
import { performTokenRefresh } from "@/libs/axios";
import { SessionAuthCookies } from "@/libs/cookies";
import { AuthState } from "@/utils/auth-state";
import { calculateMaxAge } from "@/utils/calculate-max-age";
import { decodeJwt } from "@/utils/jwt";

type SessionState = {
  accessToken: string | null;
  user: TUserData | null;
  isAuthenticated: boolean;
};

type SessionPayload = {
  accessToken: string;
  refreshToken?: string;
  refreshExpiresAt?: string | number | Date;
};

type SessionContextType = {
  isAuthenticated: boolean;
  user: TUserData | null;
  accessToken: string | null;
  login: (payload: SessionPayload) => void;
  logout: () => Promise<void>;
  isLoading: boolean;
  /**
   * True when we're still determining authentication status (initial session check).
   * Use this to prevent API calls until we know if the user is authenticated.
   */
  isPending: boolean;
};

const SessionContext = createContext<SessionContextType | null>(null);

type SessionProviderProps = {
  children: React.ReactNode;
};

function initializeSession(): SessionState {
  AuthState.accessToken = null;
  return { accessToken: null, user: null, isAuthenticated: false };
}

function clearSession() {
  SessionAuthCookies.remove();
  SessionAuthCookies.removeRefreshToken();
  // Only update AuthState.accessToken - the request interceptor handles Authorization header
  AuthState.accessToken = null;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [sessionState, setSessionState] = useState<SessionState>(() => initializeSession());
  const [isMutating, setIsMutating] = useState(false);
  // Track if we've completed the initial session check
  const [hasCheckedSession, setHasCheckedSession] = useState(false);

  const refreshTokenValue = SessionAuthCookies.getRefreshToken();

  // If there's no refresh token, we've already checked the session (no session to restore)
  const shouldAttemptRefresh =
    Boolean(refreshTokenValue) && !sessionState.isAuthenticated && !AuthState.isLoggingIn;

  const [isRefreshingSession, setIsRefreshingSession] = useState(false);

  // Attempt session restoration on mount using the shared refresh mechanism
  useEffect(() => {
    if (!shouldAttemptRefresh || hasCheckedSession) return;

    let cancelled = false;

    async function restoreSession() {
      if (!refreshTokenValue) {
        setHasCheckedSession(true);
        return;
      }

      setIsRefreshingSession(true);

      try {
        // Use the shared performTokenRefresh to coordinate with axios interceptor
        const accessToken = await performTokenRefresh(refreshTokenValue);

        if (cancelled) return;

        if (accessToken) {
          const decodedUser = decodeJwt<TUserData>(accessToken);
          setSessionState({
            accessToken,
            user: decodedUser,
            isAuthenticated: true,
          });
        }

        setHasCheckedSession(true);
      } catch {
        if (cancelled) return;

        clearSession();
        setSessionState({ accessToken: null, user: null, isAuthenticated: false });
        setHasCheckedSession(true);
        // Note: performTokenRefresh already dispatches auth:unauthorized on failure
      } finally {
        if (!cancelled) {
          setIsRefreshingSession(false);
        }
      }
    }

    void restoreSession();

    return () => {
      cancelled = true;
    };
  }, [shouldAttemptRefresh, hasCheckedSession, refreshTokenValue]);

  // Mark session as checked when there's no refresh token to attempt
  useEffect(() => {
    if (!refreshTokenValue && !hasCheckedSession) {
      setHasCheckedSession(true);
    }
  }, [refreshTokenValue, hasCheckedSession]);

  useEffect(() => {
    function handleUnauthorized() {
      clearSession();
      setSessionState({ accessToken: null, user: null, isAuthenticated: false });

      // Redirect to login if not already on an auth route
      const pathname = window.location.pathname;
      const authRoutes = [ROUTES.AUTH.LOGIN];
      const isAuthRoute = authRoutes.some(
        (route) => pathname === route || pathname.startsWith(`${route}/`)
      );

      if (!isAuthRoute) {
        // Use window.location.href for hard navigation (clears all in-memory state)
        window.location.href = `${ROUTES.AUTH.LOGIN}?redirect=${encodeURIComponent(pathname)}`;
      }
    }

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, []);

  function login({ accessToken, refreshToken, refreshExpiresAt }: SessionPayload) {
    try {
      setIsMutating(true);
      AuthState.isLoggingIn = true;

      clearSession();

      const decodedUser = decodeJwt<TUserData>(accessToken);

      // Only update AuthState.accessToken - the request interceptor handles Authorization header
      AuthState.accessToken = accessToken;

      if (refreshToken) {
        const refreshMaxAge = calculateMaxAge(refreshExpiresAt);
        SessionAuthCookies.setRefreshToken(refreshToken, { maxAge: refreshMaxAge });
      }

      setSessionState({
        accessToken,
        user: decodedUser,
        isAuthenticated: true,
      });

      // Mark session check as complete
      setHasCheckedSession(true);

      setTimeout(() => {
        AuthState.isLoggingIn = false;
      }, 1000);
    } catch (error) {
      console.error("Failed to establish session:", error);
      AuthState.isLoggingIn = false;
      clearSession();
      setSessionState({ accessToken: null, user: null, isAuthenticated: false });
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    } finally {
      setIsMutating(false);
    }
  }

  async function logout() {
    try {
      setIsMutating(true);
      await logoutApi().catch(() => {
        toast.error("Failed to logout");
      });
      clearSession();
      setSessionState({ accessToken: null, user: null, isAuthenticated: false });
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsMutating(false);
    }
  }

  const isLoading = isMutating || isRefreshingSession;

  // isPending is true when we're still determining authentication status
  // This happens when:
  // 1. There's a refresh token but we haven't completed the refresh yet
  // 2. We haven't marked the session check as complete
  const isPending = shouldAttemptRefresh && !hasCheckedSession;

  const value = useMemo(
    () => ({
      isAuthenticated: sessionState.isAuthenticated,
      user: sessionState.user,
      accessToken: sessionState.accessToken,
      login,
      logout,
      isLoading,
      isPending,
    }),
    [
      sessionState.isAuthenticated,
      sessionState.user,
      sessionState.accessToken,
      isLoading,
      isPending,
    ]
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionContextType {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }

  return context;
}
